/**
 * DashboardPage — Main authenticated dashboard container.
 *
 * Routes between sub-views:
 *   - overview             -> OverviewPage
 *   - applications         -> ApplicationsPage
 *   - consent-log          -> ConsentLogPage
 *   - audit-log            -> AuditLogPage
 *   - api-keys             -> ApiKeysPage
 *   - notice-manager       -> WorkflowPanel + ConsentNoticeDesigner
 *   - compliance-certificate -> CertificatesPage
 *   - settings             -> SettingsPage
 */

import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DashboardShell } from '../../components/DashboardShell';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch, listDrafts, listMembers, listNoticeVersions, rollbackNoticeVersion } from '../../lib/api';
import type { MultilingualNotice, NoticeDraftSummary, NoticeVersionSummary, Purpose } from '../../lib/types';
import { STORAGE_KEYS } from '../../lib/constants';

import { OverviewPage } from './OverviewPage';
import { ApplicationsPage } from './ApplicationsPage';
import { ConsentLogPage } from './ConsentLogPage';
import { AuditLogPage } from './AuditLogPage';
import { ApiKeysPage } from './ApiKeysPage';
import { CertificatesPage } from './CertificatesPage';
import { SettingsPage } from './SettingsPage';
import { ConsentNoticeDesigner } from './ConsentNoticeDesigner';
import { WorkflowPanel } from './WorkflowPanel';

const PATH_TO_VIEW_KEY: Record<string, string> = {
  '/dashboard': 'overview',
  '/dashboard/': 'overview',
  '/dashboard/applications': 'applications',
  '/dashboard/consent-log': 'consent-log',
  '/dashboard/audit-log': 'audit-log',
  '/dashboard/api-keys': 'api-keys',
  '/dashboard/notice-manager': 'notice-manager',
  '/dashboard/certificates': 'compliance-certificate',
  '/dashboard/settings': 'settings',
};

const VIEW_KEY_TO_PATH: Record<string, string> = {
  overview: '/dashboard',
  applications: '/dashboard/applications',
  'consent-log': '/dashboard/consent-log',
  'audit-log': '/dashboard/audit-log',
  'api-keys': '/dashboard/api-keys',
  'notice-manager': '/dashboard/notice-manager',
  'compliance-certificate': '/dashboard/certificates',
  settings: '/dashboard/settings',
};

export function DashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, organisations, activeOrgId } = useAuth();
  const { addToast } = useToast();

  // Determine current view from pathname
  const getViewFromPath = (path: string) => {
    return PATH_TO_VIEW_KEY[path] || 'overview';
  };

  const [currentView, setCurrentView] = useState(() => getViewFromPath(location.pathname));

  useEffect(() => {
    const view = getViewFromPath(location.pathname);
    setCurrentView(view);
  }, [location.pathname]);

  const handleNavigate = (viewKey: string) => {
    setCurrentView(viewKey);
    const targetPath = VIEW_KEY_TO_PATH[viewKey] || '/dashboard';
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  // State for Notice Manager view
  const [notices, setNotices] = useState<MultilingualNotice | null>(null);
  const [noticeVersions, setNoticeVersions] = useState<NoticeVersionSummary[]>([]);
  const [noticeVersionsLoading, setNoticeVersionsLoading] = useState(false);
  const [noticeVersionsError, setNoticeVersionsError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<NoticeDraftSummary[]>([]);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<'owner' | 'member'>('member');
  const [isPublishingNotice, setIsPublishingNotice] = useState(false);
  const [isRollingBackNotice, setIsRollingBackNotice] = useState(false);

  const activeAppId = localStorage.getItem(STORAGE_KEYS.APP_ID) || '';

  // Fetch data specifically for Notice Manager view when active
  const fetchNoticeData = useCallback(async () => {
    if (!activeAppId) return;

    try {
      setNoticeVersionsLoading(true);
      setNoticeVersionsError(null);
      const versionsData = await listNoticeVersions(activeAppId);
      setNoticeVersions(versionsData.notices || []);
    } catch {
      setNoticeVersionsError('Failed to load version history.');
    } finally {
      setNoticeVersionsLoading(false);
    }

    try {
      const noticeData = await apiFetch<MultilingualNotice>(`/v1/notices/${activeAppId}`);
      setNotices(noticeData);
    } catch {
      // Non-critical
    }

    try {
      setDraftsLoading(true);
      setDraftsError(null);
      const draftsData = await listDrafts(activeAppId);
      setDrafts(draftsData.drafts || []);
    } catch {
      setDraftsError('Failed to load drafts.');
    } finally {
      setDraftsLoading(false);
    }

    if (user && activeOrgId) {
      try {
        const membersData = await listMembers(activeOrgId);
        const allMembers = [...(membersData.members || []), ...(membersData.pendingInvitations || [])];
        const me = allMembers.find((m) => m.email === user.email);
        setUserRole(me?.role === 'owner' ? 'owner' : 'member');
      } catch {
        // Default to member
      }
    }
  }, [activeAppId, user, activeOrgId]);

  useEffect(() => {
    if (currentView === 'notice-manager') {
      fetchNoticeData();
    }
  }, [currentView, fetchNoticeData]);

  // Redirect to onboarding if user has no organisations
  useEffect(() => {
    if (organisations.length === 0 && activeOrgId === null) {
      navigate('/onboarding');
    }
  }, [organisations, activeOrgId, navigate]);

  // Publish Notice Handler
  const handlePublishNotice = async (params: {
    enTitle: string;
    enPurposes: Purpose[];
    hiTitle: string;
    hiPurposes: Purpose[];
  }) => {
    setIsPublishingNotice(true);
    try {
      await apiFetch('/v1/notices', {
        method: 'POST',
        body: {
          version: (notices?.version || 1) + 1,
          content: {
            en: { title: params.enTitle, purposes: params.enPurposes },
            hi: { title: params.hiTitle, purposes: params.hiPurposes },
          },
        },
        useApiKey: true,
      });
      addToast({ type: 'success', message: 'Notice published successfully!' });
      await fetchNoticeData();
    } catch (err) {
      addToast({
        type: 'error',
        message: 'Failed to publish notice.',
      });
    } finally {
      setIsPublishingNotice(false);
    }
  };

  // Rollback Notice Handler
  const handleRollbackNotice = async (noticeId: string, fromVersion: number) => {
    setIsRollingBackNotice(true);
    try {
      const result = await rollbackNoticeVersion(noticeId);
      addToast({
        type: 'success',
        message: `Rolled back from v${fromVersion}. New version v${result.version} is live.`,
      });
      await fetchNoticeData();
    } catch (err) {
      addToast({
        type: 'error',
        message: 'Failed to roll back notice.',
      });
    } finally {
      setIsRollingBackNotice(false);
    }
  };

  return (
    <DashboardShell currentView={currentView} onNavigate={handleNavigate} activeAppId={activeAppId}>
      {currentView === 'overview' && <OverviewPage />}
      {currentView === 'applications' && <ApplicationsPage />}
      {currentView === 'consent-log' && <ConsentLogPage />}
      {currentView === 'audit-log' && <AuditLogPage />}
      {currentView === 'api-keys' && <ApiKeysPage />}
      {currentView === 'compliance-certificate' && <CertificatesPage />}
      {currentView === 'settings' && <SettingsPage />}

      {currentView === 'notice-manager' && (
        <div className="space-y-8">
          <WorkflowPanel
            drafts={drafts}
            draftsLoading={draftsLoading}
            draftsError={draftsError}
            userRole={userRole}
            onWorkflowChanged={fetchNoticeData}
          />
          <ConsentNoticeDesigner
            notices={notices}
            isLoading={noticeVersionsLoading}
            onPublish={handlePublishNotice}
            isPublishing={isPublishingNotice}
            onRollback={handleRollbackNotice}
            isRollingBack={isRollingBackNotice}
            noticeVersions={noticeVersions}
            noticeVersionsLoading={noticeVersionsLoading}
            noticeVersionsError={noticeVersionsError}
            applicationId={activeAppId}
          />
        </div>
      )}
    </DashboardShell>
  );
}
